import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  TagIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  thunkFetchCoupons,
  thunkToggleCouponStatus,
  thunkDeleteCoupon,
  thunkCreateCoupon,
  thunkUpdateCoupon
} from '../../redux/slices/couponSlice';

import CouponForm from './CouponForm';
import { useAdminLayout } from "../../context/adminLayoutUseContext";
const CouponManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { coupons, loading, totalPages, error } = useSelector(state => state.coupon);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
   const { setLayoutConfig } = useAdminLayout();

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    coupon_type: '',
    is_expired: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const params = {
      page: currentPage,
      page_size: 20,
      ...(searchTerm && { search: searchTerm }),
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
    };
    dispatch(thunkFetchCoupons(params));
  }, [dispatch, currentPage, searchTerm, filters]);


    useEffect(() => {
      setLayoutConfig({
        title: "Coupon / Coupon Management",
        showBackButton: true,
        scrollable: true,
      });
     
    }, [dispatch,  setLayoutConfig]);

  // const fetchCoupons = async () => {
  //   setLoading(true);
  //   try {
  //     const params = new URLSearchParams({
  //       page: currentPage,
  //       page_size: 20,
  //       ...(searchTerm && { search: searchTerm }),
  //       ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
  //     });

  //     const response = await fetch(`/coupon-management/api/coupons/?${params}`, {
  //       headers: {
  //         'Authorization': `Bearer ${localStorage.getItem('authToken')}`
  //       }
  //     });

  //     if (response.ok) {
  //       const data = await response.json();
  //       setCoupons(data.results);
  //       setTotalPages(Math.ceil(data.count / 20));
  //     }
  //   } catch (error) {
  //     console.error('Error fetching coupons:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleNavigateToCouponDashboard = () => {
    navigate('/admin/coupon/dashboard');
  }

  const handleNavigateToCouponAnalytics = () => {
    navigate('/admin/coupon/analytics');
  }

  const handleNavigateToBulkGenerate = () => {
    navigate('/admin/coupon/bulk-generate');
  }

  const handleNewCoupon = () => {
    setEditingCoupon(null); // Not editing, creating new
    setShowForm(true);
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleStatusToggle = async (coupon) => {
    await dispatch(thunkToggleCouponStatus(coupon.id));
    // Optionally re-fetch or update state
    dispatch(thunkFetchCoupons({
      page: currentPage,
      page_size: 20,
      ...(searchTerm && { search: searchTerm }),
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
    }));
  };

  const handleDelete = async (couponId) => {
    await dispatch(thunkDeleteCoupon(couponId));
    // Optionally re-fetch or update state
    dispatch(thunkFetchCoupons({
      page: currentPage,
      page_size: 20,
      ...(searchTerm && { search: searchTerm }),
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
    }));
  };

  const handleSaveCoupon = async (formData) => {
    if (editingCoupon) {
      // Edit mode
      await dispatch(thunkUpdateCoupon({ couponId: editingCoupon.id, couponData: formData }));
    } else {
      // Create mode
      await dispatch(thunkCreateCoupon(formData));
    }
    setShowForm(false);
    // Refresh list
    dispatch(thunkFetchCoupons({
      page: currentPage,
      page_size: 20,
      ...(searchTerm && { search: searchTerm }),
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
    }));
  };

  const getStatusBadge = (coupon) => {
    if (coupon.is_expired) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Expired</span>;
    }

    switch (coupon.status) {
      case 'active':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>;
      case 'inactive':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Inactive</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{coupon.status}</span>;
    }
  };

  const formatDiscount = (coupon) => {
    if (coupon.coupon_type === 'percentage') {
      return `${coupon.discount_value}%`;
    } else if (coupon.coupon_type === 'fixed_amount') {
      return `${coupon.discount_value}`;
    } else if (coupon.coupon_type === 'free_shipping') {
      return 'Free Shipping';
    }
    return coupon.discount_value;
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Coupon Management</h1>
            <p className="text-gray-600 mt-2">Create and manage promotional coupons</p>
          </div>

        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search coupons by code, name, or description..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={filters.coupon_type}
                onChange={(e) => handleFilterChange('coupon_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="percentage">Percentage</option>
                <option value="fixed_amount">Fixed Amount</option>
                <option value="free_shipping">Free Shipping</option>
                <option value="buy_x_get_y">Buy X Get Y</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Status</label>
              <select
                value={filters.is_expired}
                onChange={(e) => handleFilterChange('is_expired', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="false">Valid</option>
                <option value="true">Expired</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Coupons Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valid Until
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {coupons.map((coupon) => (
                    <tr key={coupon.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 font-mono">
                          {coupon.code}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{coupon.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {coupon.coupon_type_display}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDiscount(coupon)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {coupon.current_uses}
                        {coupon.max_uses && ` / ${coupon.max_uses}`}
                        {coupon.usage_percentage > 0 && (
                          <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                            <div
                              className="bg-blue-600 h-1 rounded-full"
                              style={{ width: `${Math.min(coupon.usage_percentage, 100)}%` }}
                            ></div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(coupon.end_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(coupon)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedCoupon(coupon);
                              setShowModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>

                          <button
                            onClick={() => {
                              setEditingCoupon(coupon);
                              setShowForm(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>

                          <button
                            onClick={() => handleStatusToggle(coupon)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Toggle Status"
                          >
                            <TagIcon className="h-4 w-4" />
                          </button>

                          <button
                            onClick={() => handleDelete(coupon.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Coupon Details Modal */}
      {showModal && selectedCoupon && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Coupon Details</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Code:</span>
                <span className="ml-2 font-mono">{selectedCoupon.code}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Type:</span>
                <span className="ml-2">{selectedCoupon.coupon_type_display}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Discount:</span>
                <span className="ml-2">{formatDiscount(selectedCoupon)}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Status:</span>
                <span className="ml-2">{getStatusBadge(selectedCoupon)}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Valid From:</span>
                <span className="ml-2">{new Date(selectedCoupon.start_date).toLocaleDateString()}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Valid Until:</span>
                <span className="ml-2">{new Date(selectedCoupon.end_date).toLocaleDateString()}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Current Uses:</span>
                <span className="ml-2">{selectedCoupon.current_uses}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Max Uses:</span>
                <span className="ml-2">{selectedCoupon.max_uses || 'Unlimited'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Min Order:</span>
                <span className="ml-2">${selectedCoupon.minimum_order_amount}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Total Discount Given:</span>
                <span className="ml-2">${selectedCoupon.total_discount_given}</span>
              </div>
              {selectedCoupon.description && (
                <div className="col-span-2">
                  <span className="font-medium text-gray-700">Description:</span>
                  <p className="ml-2 mt-1">{selectedCoupon.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <CouponForm
        isOpen={showForm}
        coupon={editingCoupon}
        onSave={handleSaveCoupon}
        onCancel={() => setShowForm(false)}
      />
    </div>

  );
};

export default CouponManagement;